import { EMEARegion } from "@dana/Common/Constants";
import InvoiceRecepients from "@dana/Components/OrderHistory/InvoiceRecepients";
import { PrintButton } from "@dana/Components/PrintButton/PrintButton";
import StyledTable from "@dana/Components/StyledTable/StyledTable";
import { themeColors } from "@dana/MobiusOverrides/styles/colors";
import { fontSizes, fontWeights, lineHeights } from "@dana/MobiusOverrides/styles/typography";
import { InvoiceDetailsResponse } from "@dana/Types/ApiModels";
import { formatDecimal } from "@dana/utils/utils";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Website } from "@insite/client-framework/Services/WebsiteService";
import translate from "@insite/client-framework/Translate";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useRef } from "react";
import { css } from "styled-components";

// --- Props Interface ---
interface ProformaInvoiceProps {
    proformainvoiceData: InvoiceDetailsResponse | null;
    websiteData: Website;
    selectedRegion:string | undefined;
}

// --- Styles Interfaces and Objects ---
export interface ProformaInvoiceStyles {
    invoiceContainer?: InjectableCss;
}

export const ProformaInvoiceStyles: ProformaInvoiceStyles = {
    invoiceContainer: {
        css: css`
            font-size: ${fontSizes.small};
            line-height: ${lineHeights.small};
            .invoice-actions {
                display: flex;
                align-items: center;
                justify-content: flex-end;
                gap: 16px;
                padding: 16px 0;
            }
            table {
                width: 100%;

                .price-cell {
                    text-align: right;
                }
            }

            td.company-logo {
                width: 200px;
            }

            td.reprint-text {
                width: 180px;
                text-align: right;
                vertical-align: top;
            }

            .address,
            address {
                font-style: normal;
            }

            .invoice-address-table {
                .section-left {
                    width: 60%;
                }

                .section-right {
                    width: 40%;
                }
            }

            .company-address {
                h3 {
                    font-weight: ${fontWeights.semiBold};
                }
            }

            .printtype-text {
                font-size: ${fontSizes.large};
                line-height: ${lineHeights.large};
                font-weight: ${fontWeights.semiBold};
                color: ${themeColors.primary.danaBlue};
                text-transform: uppercase;
                margin-bottom: 16px;
            }

            .print-text {
                font-weight: ${fontWeights.semiBold};
                text-transform: uppercase;
            }

            .header-table {
                margin-bottom: 24px;
            }
        `,
    },
};

const styles = ProformaInvoiceStyles;

// --- Component Definition ---
const ProformaInvoice: FC<ProformaInvoiceProps> = ({ proformainvoiceData, websiteData,selectedRegion }) => {
    const modalContentRef = useRef<HTMLDivElement>(null);
    const totalPrice = proformainvoiceData?.invoiceInfo?.reduce((sum, item) => sum + (item?.netAmount ?? 0), 0) ?? 0;

    const totalDiscount = proformainvoiceData?.invoiceInfo?.reduce((sum, item) => sum + (item?.discount ?? 0), 0) ?? 0;

    const ifni = proformainvoiceData?.invoiceInfo;
    const totalTariff = (ifni ?? []).reduce((sum, inv) => {
        const parts = Array.isArray(inv.partPriceInfo) ? inv.partPriceInfo : [];
        const tariffSumForLine = parts
            .filter(
                p =>
                    String(p.priceType ?? "")
                        .trim()
                        .toLowerCase() === "tariff",
            )
            .reduce((s, p) => s + (Number(p.amount) || 0), 0);
        return sum + tariffSumForLine;
    }, 0);

    const totalAmt = totalPrice + totalTariff - totalDiscount;

    return (
        <StyledWrapper className="invoice-container" {...styles.invoiceContainer} ref={modalContentRef}>
            <div className="invoice-actions text-right">
                {proformainvoiceData !== null && (
                    <InvoiceRecepients
                        invoicetype="proforma"
                        invoiceData={proformainvoiceData}
                        contentRef={modalContentRef}
                    />
                )}
                <PrintButton label={translate("Print Proforma Invoice")} targetRef={modalContentRef} />
            </div>
            {/* Header */}
            <table className="header-table">
                <tbody>
                    <tr>
                        <td className="company-logo">
                            <img src={websiteData.companyLogoImagePath} alt="Company Logo" className="company-logo" />
                        </td>
                        <td className="company-address">
                             <address>
                                <h3>{proformainvoiceData?.shipPlantName}</h3>
                                <p>
                                    {proformainvoiceData?.shipPlantAddress1}
                                    {(
                                        proformainvoiceData?.shipPlantCity ||
                                        proformainvoiceData?.shipPlantState ||
                                        proformainvoiceData?.shipPlantZip
                                    ) && (
                                            <>
                                                <br />
                                                {[
                                                    proformainvoiceData?.shipPlantCity,
                                                    proformainvoiceData?.shipPlantState,
                                                    proformainvoiceData?.shipPlantZip,
                                                ]
                                                    .filter(Boolean)
                                                    .join(" ")}
                                            </>
                                        )}
                                    {websiteData?.companyUrl && (
                                        <>
                                            <br />
                                            {websiteData.companyUrl}
                                        </>
                                    )}
                                </p>
                            </address>
                        </td>
                        <td className="reprint-text">
                            <Typography forwardAs="p" className="printtype-text">
                                {translate("Proforma Invoice")}
                            </Typography>
                            <Typography forwardAs="p" className="print-text">
                                {translate("Reprint")}
                            </Typography>
                        </td>
                    </tr>
                </tbody>
            </table>

            <table className="invoice-address-table">
                <tbody>
                    <tr>
                        <td valign="top" className="section-left">
                            {/* Sold To and Ship To */}
                            <table className="address-table">
                                <tbody>
                                    <tr>
                                        <td>
                                            <div className="invoice_card">
                                                <div className="invoice_card_header">
                                                    {translate("Bill To Party")}:{proformainvoiceData?.billTo}
                                                </div>
                                                <div className="invoice_card_body">
                                                    <address>
                                                        <p>
                                                            {proformainvoiceData?.billName}
                                                            {proformainvoiceData?.billAddress1 && (
                                                                <>
                                                                    <br />
                                                                    {proformainvoiceData.billAddress1}
                                                                </>
                                                            )}
                                                            {(
                                                                proformainvoiceData?.billCity ||
                                                                proformainvoiceData?.billState ||
                                                                proformainvoiceData?.billZip
                                                            ) && (
                                                                <>
                                                                    <br />
                                                                    {[
                                                                        proformainvoiceData?.billCity,
                                                                        proformainvoiceData?.billState,
                                                                        proformainvoiceData?.billZip,
                                                                    ]
                                                                        .filter(Boolean)
                                                                        .join(" ")}
                                                                </>
                                                            )}
                                                        </p>
                                                    </address>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="invoice_card">
                                                <div className="invoice_card_header">
                                                    {translate("Ship To Party")}:{proformainvoiceData?.shipTo}
                                                </div>
                                                <div className="invoice_card_body">
                                                    <address>
                                                        <p>
                                                            {proformainvoiceData?.shipName}
                                                            {proformainvoiceData?.shipAddress1 && (
                                                                <>
                                                                    <br />
                                                                    {proformainvoiceData.shipAddress1}
                                                                </>
                                                            )}
                                                            {(
                                                                proformainvoiceData?.shipCity ||
                                                                proformainvoiceData?.shipState ||
                                                                proformainvoiceData?.shipZip
                                                            ) && (
                                                                    <>
                                                                        <br />
                                                                        {[
                                                                            proformainvoiceData?.shipCity,
                                                                            proformainvoiceData?.shipState,
                                                                            proformainvoiceData?.shipZip,
                                                                        ]
                                                                            .filter(Boolean)
                                                                            .join(" ")}
                                                                    </>
                                                                )}
                                                        </p>
                                                    </address>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <div className="invoice_card">
                                                <div className="invoice_card_header">
                                                    {translate("Sold To Party")}: {proformainvoiceData?.mailTo}
                                                </div>
                                                <div className="invoice_card_body">
                                                    <div className="address">
                                                        <p>
                                                            {proformainvoiceData?.mailName}
                                                            {proformainvoiceData?.mailAddress1 && (
                                                                <>
                                                                    <br />
                                                                    {proformainvoiceData.mailAddress1}
                                                                </>
                                                            )}
                                                            {(
                                                                proformainvoiceData?.mailCity ||
                                                                proformainvoiceData?.mailState ||
                                                                proformainvoiceData?.mailZip
                                                            ) && (
                                                                <>
                                                                    <br />
                                                                    {[
                                                                        proformainvoiceData?.mailCity,
                                                                        proformainvoiceData?.mailState,
                                                                        proformainvoiceData?.mailZip,
                                                                    ]
                                                                        .filter(Boolean)
                                                                        .join(" ")}
                                                                </>
                                                            )}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="invoice_card">
                                                <div className="invoice_card_header">
                                                    {translate("Remit To Party")}:{" "}
                                                    {proformainvoiceData?.remitToCustomerId}
                                                </div>
                                                <div className="invoice_card_body">
                                                    <address>
                                                        <p>
                                                            {proformainvoiceData?.remitToName}
                                                            {proformainvoiceData?.remitToAddress1 && (
                                                                <>
                                                                    <br />
                                                                    {proformainvoiceData.remitToAddress1}
                                                                </>
                                                            )}
                                                            {(
                                                                proformainvoiceData?.remitToCity ||
                                                                proformainvoiceData?.remitToState ||
                                                                proformainvoiceData?.remitToZip
                                                            ) && (
                                                                <>
                                                                    <br />
                                                                    {[
                                                                        proformainvoiceData?.remitToCity,
                                                                        proformainvoiceData?.remitToState,
                                                                        proformainvoiceData?.remitToZip,
                                                                    ]
                                                                        .filter(Boolean)
                                                                        .join(" ")}
                                                                </>
                                                            )}
                                                        </p>
                                                    </address>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </td>
                        <td valign="top" className="section-right">
                            {/* Delivery Information */}
                            <table className="delivery-information-table">
                                <tbody>
                                    <tr>
                                        <td>
                                            <div className="invoice_card">
                                                <div className="invoice_card_header">{translate("Information")}</div>
                                                <div className="invoice_card_body">
                                                    <table className="text-body-sm">
                                                        <tbody>
                                                            <tr>
                                                                <td className="font-semibold">
                                                                    {translate("Invoice No.")}
                                                                </td>
                                                                <td>{proformainvoiceData?.invoiceNumber}</td>
                                                            </tr>
                                                            <tr>
                                                                <td className="font-semibold">
                                                                    {translate("Invoice Date")}
                                                                </td>
                                                                <td>{proformainvoiceData?.invoiceDate?.split('T')[0]}</td>
                                                            </tr>
                                                            <tr>
                                                                <td className="font-semibold">
                                                                    {translate("Ref Sales Order No.")}
                                                                </td>
                                                                <td>{proformainvoiceData?.documentNumber}</td>
                                                            </tr>
                                                            <tr>
                                                                <td className="font-semibold">
                                                                    {translate("Customer Account No")}
                                                                </td>
                                                                <td>{proformainvoiceData?.billTo}</td>
                                                            </tr>
                                                            <tr>
                                                                <td className="font-semibold">
                                                                    {translate("Currency")}
                                                                </td>
                                                                <td>{proformainvoiceData?.currency}</td>
                                                            </tr>
                                                            <tr>
                                                                <td className="font-semibold">
                                                                    {translate("Terms of Payment")}
                                                                </td>
                                                                <td>{proformainvoiceData?.termsDescription}</td>
                                                            </tr>
                                                            <tr>
                                                                <td className="font-semibold">
                                                                    {translate("Incortem")}
                                                                </td>
                                                                <td>{proformainvoiceData?.incotermsDescription}</td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </td>
                    </tr>
                </tbody>
            </table>

            {/* Line Item Table */}
            <StyledTable>
                <thead>
                    <tr>
                        <th>{translate("Line Item")}</th>
                        <th>{translate("Material / Description")}</th>
                        <th>{translate("Quantity")}</th>
                        <th>
                            {translate("Unit Price")} ({proformainvoiceData?.currency})
                        </th>
                        {(selectedRegion!==EMEARegion) && ( 
                            <>
                        <th>
                            {translate("Unit Tariff Fee")} ({proformainvoiceData?.currency})
                        </th>
                        <th>
                            {translate("Unit Core Deposit")} ({proformainvoiceData?.currency})
                        </th>
                        </>
                        )}
                        <th>
                            {translate("Extended Price")} ({proformainvoiceData?.currency})
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {proformainvoiceData?.invoiceInfo?.map((item, index) => (
                        <tr key={index}>
                            <td>{item.lineNumber}</td>
                            <td>
                                <div>
                                    <p>{item.partNumber}</p>
                                    <p>{item.partDescription}</p>
                                    <p>
                                        {translate("Cust. Part No.")}: {item.customerPartNumber}
                                    </p>
                                    <p>
                                        Order {item.orderNumber} from {item.orderDate}
                                    </p>
                                </div>
                            </td>
                            <td className="price-cell">{item.quantityOrdered}</td>
                            <td className="price-cell">{selectedRegion===EMEARegion?formatDecimal(item.pricePerUnit,{locale:"fr-FR"}):formatDecimal(item.pricePerUnit)}</td>
                            {(selectedRegion!==EMEARegion) && ( 
                                <>
                            <td className="price-cell">
                                {formatDecimal(item.partPriceInfo?.find(p => p.priceType === "Tariff")?.amount ?? 0)}
                            </td>
                            <td className="price-cell">
                                {" "}
                                {formatDecimal(item.partPriceInfo?.find(p => p.priceType === "Core Fee")?.amount ?? 0)}
                            </td>
                            </>
                            )}
                            <td className="price-cell">{selectedRegion===EMEARegion?formatDecimal(item.netAmount,{locale:"fr-FR"}):formatDecimal(item.netAmount)}</td>
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    <tr>
                        <td colSpan={4}></td>
                        <td colSpan={2}>
                            <strong>
                                {translate("Item(s) Total")}({proformainvoiceData?.currency})
                            </strong>
                        </td>
                        <td className="price-cell">{selectedRegion===EMEARegion?formatDecimal(totalPrice,{locale:"fr-FR"}):formatDecimal(totalPrice)}</td>
                    </tr>
                    <tr>
                        <td colSpan={4}></td>
                        {(selectedRegion!==EMEARegion) && ( 
                        <td colSpan={2}>
                            <strong>
                                {translate("Tariff Fee Total")} ({proformainvoiceData?.currency})
                            </strong>
                        </td>
                        )}
                        <td className="price-cell">{formatDecimal(totalTariff)}</td>
                    </tr>
                    <tr>
                        <td colSpan={4}></td>
                        <td colSpan={2}>
                            <strong>{translate("Discount")}</strong>
                        </td>
                        <td className="price-cell">-{selectedRegion===EMEARegion?formatDecimal(totalDiscount,{locale:"fr-FR"}):formatDecimal(totalDiscount)}</td>
                    </tr>
                    <tr>
                        <td colSpan={4}></td>
                        <td colSpan={2}>
                            <strong>
                                {translate("Total Amount")}({proformainvoiceData?.currency})
                            </strong>
                        </td>
                        <td className="price-cell">{selectedRegion===EMEARegion?formatDecimal(totalAmt,{locale:"fr-FR"}):formatDecimal(totalAmt)}</td>
                    </tr>
                </tfoot>
            </StyledTable>
        </StyledWrapper>
    );
};

export default ProformaInvoice;
